import { useEffect, useState } from "react";
import { Badge, Col, ProgressBar, Row } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { formatNumberWithTwoDecimals } from "../../../../../utils/FormatUtils";
import { formatDateToYYYYMMDD } from "../../../../../utils/DateUtils";
import FormSection from "../../../Common/Form/FormSection";
import { useTranslation } from "react-i18next";
import { getSubscriptionSituation } from "../../Subscription/subscriptionSituation";

// Selo de cada tonalidade da regra comum, com as mesmas cores do selo na listagem de
// assinaturas: em dia em verde, pendente em amarelo, em atraso em vermelho, encerrada neutra.
const SITUATION_BADGE = {
    active: { bg: 'success' },
    pending: { bg: 'warning', text: 'dark', warning: true },
    overdue: { bg: 'danger', warning: true },
    inactive: { bg: 'secondary' },
};

/**
 * Resumo do clube na ficha do cliente: qual plano, como está a cota, quando o ciclo vence e a
 * situação das cobranças.
 *
 * Somente leitura e sem ações: a ficha do cliente é onde se confere o cadastro. Quem altera
 * contrato, plano ou cobrança é a tela de assinaturas — ter dois lugares que editam a mesma
 * coisa é o caminho para eles discordarem.
 */
const CustomerSubscriptionSummary = ({ customerId }) => {

    const { t } = useTranslation();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!customerId) {
            setSubscription(null);
            return;
        }

        let active = true;
        setLoading(true);

        axiosInstance.get(`${Api.subscription.subscription}/customer/id/${customerId}`)
            .then(resp => {
                if (active) setSubscription(resp.data || null);
            })
            // 404 é o cliente sem contrato de clube, que é a maioria: não é erro e não pode
            // aparecer como falha na tela de cadastro.
            .catch(() => {
                if (active) setSubscription(null);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => { active = false; };
    }, [customerId]);

    // Enquanto carrega não mostra nada: um esqueleto piscando abriria e fecharia espaço na
    // ficha para a maioria dos clientes, que não é do clube.
    if (loading || !subscription) {
        return null;
    }

    const formatDate = (value) => {
        if (!value) return null;
        const [year, month, day] = String(value).substring(0, 10).split('-');
        return `${day}/${month}/${year}`;
    };

    /** Dia e mês, sem o ano: o ciclo é curto e o ano só encompridaria a frase. */
    const formatDayMonth = (value) => {
        if (!value) return null;
        const [, month, day] = String(value).substring(0, 10).split('-');
        return `${day}/${month}`;
    };

    const usage = subscription.planBenefitUsage;

    // Mesma situação da tela de Assinaturas e do detalhe do plano na venda. Antes a ficha
    // olhava só a cobrança em atraso: quem nunca pagou aparecia "Em dia" aqui e "Aguardando
    // pagamento" na venda.
    const situation = getSubscriptionSituation(subscription);
    const situationBadge = SITUATION_BADGE[situation.tone] || SITUATION_BADGE.inactive;
    // Em atraso mantém a contagem, que a regra comum não traz no rótulo.
    const situationLabel = situation.key === 'overdue'
        ? (subscription.overdueChargeCount > 1
            ? `${subscription.overdueChargeCount} cobranças em atraso`
            : 'Cobrança em atraso')
        : t(situation.key);
    const nextChargeDate = formatDate(subscription.nextChargeDueDate);

    /**
     * O que mostrar em "Vencimento": a data do ciclo do cliente sempre em destaque, e a
     * explicação, quando há, na linha de baixo.
     *
     * Antes o campo se chamava "Paga de novo em" e, sem cobrança futura registrada, trocava a
     * data por frases ("Nenhuma cobrança gerada", "Última cobrança falhou"). Quem atende
     * queria saber quando o ciclo do cliente vence e ficava sem a data.
     */
    const resolveNextCharge = () => {
        // Pacote da loja não tem próxima cobrança: ninguém cobra o cliente, ele renova se
        // quiser. O que a ficha precisa dizer é até quando o pacote vale.
        if (subscription.chargedAtStore) {
            return {
                label: formatDate(subscription.storePackageValidUntil) || '-',
                hint: 'Pacote pago na loja. Para continuar, renove em Assinaturas.'
            };
        }

        if (nextChargeDate) {
            return {
                label: nextChargeDate,
                amount: subscription.nextChargeAmount > 0
                    ? formatNumberWithTwoDecimals(subscription.nextChargeAmount)
                    : null
            };
        }

        const lastChargeDate = formatDate(subscription.lastChargeDueDate);

        // Cobrança recusada trava o ciclo seguinte: o vencimento que importa é o da cobrança
        // parada, e o destaque evita que o contrato travado passe por quem só espera a data.
        if (subscription.lastChargeStatus === 'FAILED') {
            return {
                label: lastChargeDate || 'Última cobrança falhou',
                hint: 'A última cobrança foi recusada. A próxima só é gerada depois de regularizar.',
                warning: true
            };
        }

        // Cobrança em aberto que já venceu: existe cobrança, ela só não foi paga.
        if (['PENDING', 'PROCESSING', 'OVERDUE'].includes(subscription.lastChargeStatus)) {
            return {
                label: lastChargeDate || 'Cobrança vencida em aberto',
                hint: 'Venceu e está em aberto.',
                warning: true
            };
        }

        // Última mensalidade paga e a seguinte ainda não registrada: o vencimento é previsto
        // pela periodicidade do plano. As datas da API vêm em AAAA-MM-DD e comparam como texto.
        const forecastIso = subscription.nextChargeForecastDate
            ? String(subscription.nextChargeForecastDate).substring(0, 10)
            : null;
        if (forecastIso) {
            // Previsão já vencida é o ciclo que venceu sem a cobrança chegar do provedor:
            // aparece como pendência, não como próximo pagamento.
            if (forecastIso < formatDateToYYYYMMDD(new Date())) {
                return {
                    label: formatDate(forecastIso),
                    hint: 'O ciclo venceu e a cobrança ainda não chegou do provedor.',
                    warning: true
                };
            }
            return { label: formatDate(forecastIso), hint: 'Previsto. A cobrança ainda não foi gerada.' };
        }

        // Última cobrança cancelada, estornada ou com link expirado: não há o que vencer, mas
        // a data da última ainda situa quem atende.
        if (lastChargeDate) {
            return { label: 'Nenhuma cobrança em aberto', hint: `Último vencimento em ${lastChargeDate}.` };
        }

        // Só sobra quem realmente não tem cobrança nenhuma registrada.
        return { label: 'Nenhuma cobrança gerada' };
    };

    const nextCharge = resolveNextCharge();

    // O período vem escrito porque "neste ciclo" não diz nada a quem atende: o ciclo é
    // ancorado na data de contratação, então cada cliente vira num dia diferente do mês. Sem
    // as datas o campo ainda funciona, só fica sem o recorte.
    const cyclePeriod = usage?.periodStart && usage?.periodEnd
        ? `Ciclo de ${formatDayMonth(usage.periodStart)} a ${formatDayMonth(usage.periodEnd)}.`
        : null;

    /**
     * Cota do ciclo como campo próprio, no mesmo nível de plano, vencimento e situação. Era uma
     * linha cinza no rodapé do card, e é o que quem atende mais procura: quanto o cliente ainda
     * pode usar do plano.
     */
    const resolveUsage = () => {
        if (!usage) return null;

        const used = Number(usage.usedBenefits) || 0;
        const usedWord = used === 1 ? 'usado' : 'usados';
        const hint = (...parts) => parts.filter(Boolean).join(' ');

        // Plano sem limite não tem barra: não existe um total para comparar.
        if (usage.unlimited) {
            return { label: `${used} ${usedWord}`, hint: hint('Plano sem limite.', cyclePeriod), percent: null };
        }

        const max = Number(usage.maxBenefits) || 0;
        const remaining = Math.max(max - used, 0);
        const percent = max > 0 ? Math.min((used / max) * 100, 100) : 100;

        // Cota esgotada é o que muda o atendimento, por isso ganha o mesmo alerta das pendências.
        if (remaining === 0) {
            return {
                label: `${used} de ${max} ${usedWord}`,
                hint: hint('Limite do ciclo atingido.', cyclePeriod),
                percent,
                warning: true
            };
        }

        return {
            label: `${used} de ${max} ${usedWord}`,
            hint: hint(remaining === 1 ? 'Resta 1.' : `Restam ${remaining}.`, cyclePeriod),
            percent
        };
    };

    const usageInfo = resolveUsage();

    // Com a cota são quatro campos: dois por linha no tablet e todos lado a lado no desktop.
    // No celular cada campo ocupa a linha inteira.
    const columnSize = usageInfo ? { sm: 6, lg: 3 } : { md: 4 };

    return (
        <FormSection
            title="Clube"
            description="Assinatura ativa do cliente. Alterações são feitas na tela de assinaturas."
            icon="fa-solid fa-crown"
            compact
        >
            <Row className="g-3">
                <Col {...columnSize}>
                    <small className="text-muted d-block">Plano</small>
                    <strong>{subscription.plan?.name || '-'}</strong>
                </Col>

                {usageInfo && (
                    <Col {...columnSize}>
                        <small className="text-muted d-block">Benefícios no ciclo</small>
                        <strong className={usageInfo.warning ? 'text-warning' : ''}>
                            {usageInfo.warning && <i className="fa fa-triangle-exclamation me-1"></i>}
                            {usageInfo.label}
                        </strong>
                        {usageInfo.percent !== null && (
                            <ProgressBar
                                now={usageInfo.percent}
                                variant={usageInfo.warning ? 'warning' : 'success'}
                                className="mt-1"
                                style={{ height: 6 }}
                            />
                        )}
                        <small className="text-muted d-block">{usageInfo.hint}</small>
                    </Col>
                )}

                <Col {...columnSize}>
                    <small className="text-muted d-block">
                        {subscription.chargedAtStore ? 'Pacote válido até' : 'Vencimento'}
                    </small>
                    <strong className={nextCharge.warning ? 'text-warning' : ''}>
                        {nextCharge.warning && <i className="fa fa-triangle-exclamation me-1"></i>}
                        {nextCharge.label}
                        {nextCharge.amount && (
                            <span className="text-muted fw-normal"> (R$ {nextCharge.amount})</span>
                        )}
                    </strong>
                    {nextCharge.hint && (
                        <small className="text-muted d-block">{nextCharge.hint}</small>
                    )}
                </Col>

                <Col {...columnSize}>
                    <small className="text-muted d-block">Situação</small>
                    <Badge bg={situationBadge.bg} text={situationBadge.text}>
                        {situationBadge.warning && <i className="fa fa-triangle-exclamation me-1"></i>}
                        {situationLabel}
                    </Badge>
                </Col>
            </Row>
        </FormSection>
    );
};

export default CustomerSubscriptionSummary;
